import { useTranslation } from 'react-i18next';
import type { MatchResult } from '../types';
import SkillBadge from './SkillBadge';

interface MatchCardProps {
  match: MatchResult;
  tenderId: string;
  onViewBreakdown?: (match: MatchResult) => void;
}

/** Returns Tailwind colour classes for the score arc based on score value. */
function scoreColor(score: number): string {
  if (score >= 80) return 'text-green-600';
  if (score >= 60) return 'text-amber-500';
  if (score >= 40) return 'text-orange-500';
  return 'text-red-600';
}

/**
 * MatchCard renders a single candidate match result including score,
 * matched/missing skills and a link to the full match detail.
 */
export default function MatchCard({ match, onViewBreakdown }: MatchCardProps) {
  const { t } = useTranslation();
  const MAX_VISIBLE_SKILLS = 6;
  const allMatched = [...match.matched_skills, ...match.nice_to_have_matched];
  const visibleMatched = allMatched.slice(0, MAX_VISIBLE_SKILLS);
  const hiddenMatchedCount = Math.max(0, allMatched.length - MAX_VISIBLE_SKILLS);

  const visibleMissing = match.missing_skills.slice(0, 4);
  const hiddenMissingCount = Math.max(0, match.missing_skills.length - 4);

  return (
    <article
      aria-label={t('matchCard.matchResult', { name: match.employee_name, score: match.score })}
      className="card flex flex-col gap-4"
    >
      {/* Header — employee info + score */}
      <header className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-base font-semibold text-gray-900">
            {match.employee_name}
          </h3>
          <p className="text-sm text-gray-500">{match.employee_role}</p>
          <p className="mt-1 text-xs text-gray-400">
            {t(`matchCard.tiers.${match.tier}`, match.tier)}
          </p>
        </div>

        {/* Score circle */}
        <div
          aria-label={t('matchCard.scoreLabel', { score: match.score })}
          className={[
            'flex h-16 w-16 shrink-0 flex-col items-center justify-center rounded-full border-4',
            match.score >= 80
              ? 'border-green-400'
              : match.score >= 60
              ? 'border-amber-400'
              : 'border-red-400',
          ].join(' ')}
        >
          <span className={['text-xl font-bold leading-none', scoreColor(match.score)].join(' ')}>
            {match.score}
          </span>
          <span className="text-[10px] text-gray-400">/ 100</span>
        </div>
      </header>

      {/* Matched skills */}
      {visibleMatched.length > 0 && (
        <section aria-label={t('matchCard.matchedSkills')}>
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
            {t('matchCard.matchedSkills')}
          </p>
          <ul className="flex flex-wrap gap-1.5">
            {visibleMatched.map((skill) => (
              <SkillBadge key={skill} skill={skill} matched />
            ))}
            {hiddenMatchedCount > 0 && (
              <li>
                <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
                  {t('matchCard.moreSkills', { count: hiddenMatchedCount })}
                </span>
              </li>
            )}
          </ul>
        </section>
      )}

      {/* Missing skills */}
      {visibleMissing.length > 0 && (
        <section aria-label={t('matchCard.missingSkills')}>
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
            {t('matchCard.missingSkills')}
          </p>
          <ul className="flex flex-wrap gap-1.5">
            {visibleMissing.map((skill) => (
              <SkillBadge key={skill} skill={skill} matched={false} />
            ))}
            {hiddenMissingCount > 0 && (
              <li>
                <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
                  {t('matchCard.moreSkills', { count: hiddenMissingCount })}
                </span>
              </li>
            )}
          </ul>
        </section>
      )}

      {/* AI explanation */}
      {match.explanation && (
        <blockquote className="border-l-2 border-brand-200 pl-3 text-sm italic text-gray-600">
          {match.explanation}
        </blockquote>
      )}

      {/* Footer — open breakdown modal */}
      <footer className="mt-auto pt-2 border-t border-gray-100">
        <button
          onClick={() => onViewBreakdown?.(match)}
          className="text-sm font-medium text-brand-600 hover:text-brand-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded"
          aria-label={t('matchCard.viewBreakdown', { name: match.employee_name })}
        >
          {t('matchCard.viewFullBreakdown')} &rarr;
        </button>
      </footer>
    </article>
  );
}
